import { z } from 'zod';
import { cuid } from './common.js';
/**
 * L'Assistente (os-planning/ai/PIANO.md): i corpi delle rotte /ai che usa anche il web e
 * la forma delle risposte. Gli schemi d'uscita del modello stanno nell'API
 * (apps/api/src/modules/ai/ai.schemi.ts, con zod/v4): qui solo quello che passa tra
 * browser e server.
 */
/**
 * I codici delle sezioni della scheda memoria, nell'ordine in cui si mostrano. Nel web
 * i nomi a schermo stanno in features/assistente/assistenteApi.ts (SEZIONI_MEMORIA).
 */
export const CODICI_SEZIONE = ['CHI_SONO', 'NOMI', 'PERSONE', 'ACCORDI', 'REGOLE', 'TONO', 'IN_SOSPESO', 'DECISIONI', 'CIFRE'];
// ── Dividi con l'Assistente (Fase 1.1, 1.2, 1.3) ───────────────────────────
export const dividiInput = z.object({
    /** Il dettato o il testo incollato. Più lungo di 8000 caratteri si manda a pezzi. */
    testo: z.string().trim().min(1).max(8000),
    /** Il progetto della finestra o della task di partenza: il modello non lo sceglie mai. */
    projectId: cuid().optional().nullable(),
    /** Il cliente della finestra o della task di partenza. */
    clientId: cuid().optional().nullable(),
    /** «Dividi in più task» (1.3): la task da cui si parte. */
    partenzaId: cuid().optional().nullable(),
    /** La pagina aperta, per le schede da leggere. */
    pagina: z.string().max(300).optional(),
});
// ── Riscrivi titolo e descrizione (Fase 1.4) ───────────────────────────────
export const riscriviInput = z.object({ taskId: cuid() });
// ── 2F: gli allegati della chat (os-planning/ai/fase-2f-allegati.md) ──
// Scritti prima degli agenti della 2F (29/10/2026): fissano le forme tra web e API.
/** Letti da web e API: le frasi coi numeri li prendono da qui, mai scritti a mano. */
export const LIMITI_ALLEGATI = {
    perDomanda: 5, latoFoto: 1568, kbFoto: 1024, mbOriginale: 30, megapixel: 50, mbPdf: 5, paginePdf: 10,
    tokenConversazione: 20_000, mbConversazione: 15, inAttesa: 10, oreInAttesa: 24,
    latoMiniatura: 160, kbMiniatura: 12, lungaStretta: 4,
};
/**
 * I codici degli allegati della domanda: quelli con `details.allegati` mettono il bordo rosso ai loro
 * pezzi, ALLEGATI_CONVERSAZIONE dà il tasto «Nuova conversazione». La domanda torna nel campo con
 * ogni errore che arriva prima di `conversazione`, non solo con questi.
 *
 * ALLEGATI_TROPPO_PIENI (29/10/2026, dalla revisione della 2F): gli allegati della domanda, da soli,
 * passano i tetti della conversazione (20.000 token o 15 MB), anche in una conversazione nuova. Il
 * bordo rosso va sui più pesanti, e niente «Nuova conversazione»: una conversazione nuova non
 * basterebbe, e prima il tasto rifaceva lo stesso errore all'infinito.
 */
export const CODICI_ALLEGATI_DOMANDA = [
    'ALLEGATO_SPARITO', 'ALLEGATI_CONVERSAZIONE', 'ALLEGATO_TROPPO_PIENO', 'ALLEGATI_TROPPO_PIENI', 'ALLEGATO_NON_LETTO',
];
// ── 2G: i vocali (os-planning/ai/fase-2g-vocali.md) ──
// 29/10/2026: il vocale va a POST /ai/vocali, torna testo nel campo e lo manda chi chiede. L'audio
// non si salva mai.
/** Letti da web e API: le frasi coi numeri li prendono da qui, mai scritti a mano. */
export const LIMITI_VOCALI = {
    secondi: 300, margine: 2, secondiMinimi: 1, avvisoDa: 270,
    mb: 12, mbSenzaDurata: 5, perVolta: 5, alGiorno: 40,
    kbps: 32, caratteriPrompt: 400,
};
/** I codici di POST /ai/vocali: il web sceglie da qui la frase col nome del file. */
export const CODICI_VOCALI = [
    'VOCALE_MANCA', 'VOCALE_PESO', 'VOCALE_TIPO', 'VOCALE_CORTO', 'VOCALE_LUNGO', 'VOCALE_ROVINATO',
    'VOCALE_VUOTO', 'VOCALI_OGGI', 'VOCALE_OCCUPATO', 'VOCALE_NON_RISPONDE', 'VOCALE_CHIAVE', 'VOCALI_SENZA_CHIAVE',
];
// ── 2H: la posta Google (os-planning/ai/fase-2h-mail.md) ──
// 29/10/2026: ogni Amministratore collega la sua casella; MUNE cerca, legge e prepara la mail nella
// carta, e la mail parte solo col clic su «Manda». Gli indirizzi li sceglie il server, mai il modello.
/** Letti da web e API: le frasi coi numeri li prendono da qui, mai scritti a mano. */
export const LIMITI_POSTA = {
    conversazioniPerRicerca: 10, messaggiPerConversazione: 6, caratteriPerMessaggio: 3000,
    destinatari: 10, mandateAlGiorno: 20, lettureAlGiorno: 100, minutiCarta: 30,
    caratteriOggetto: 200, caratteriTesto: 8000,
    /**
     * Gli allegati di una mail (30/10/2026): quanti, e quanti MB tutti insieme. In base64 pesano un
     * terzo in più: 18 MB restano sotto i 25 di Gmail.
     */
    allegati: 10, mbAllegati: 18,
};
/**
 * I file che Gmail non manda (support.google.com/mail/answer/6590): programmi, script e simili.
 * Il web li ferma prima di caricarli, il server di nuovo.
 */
export const ESTENSIONI_FERME_GMAIL = [
    'ade', 'adp', 'apk', 'appx', 'appxbundle', 'bat', 'cab', 'chm', 'cmd', 'com', 'cpl', 'diagcab', 'diagcfg',
    'diagpack', 'dll', 'dmg', 'ex', 'ex_', 'exe', 'hta', 'img', 'ins', 'iso', 'isp', 'jar', 'jnlp', 'js', 'jse',
    'lib', 'lnk', 'mde', 'msc', 'msi', 'msix', 'msixbundle', 'msp', 'mst', 'nsh', 'pif', 'ps1', 'scr', 'sct',
    'shb', 'sys', 'vb', 'vbe', 'vbs', 'vhd', 'vxd', 'wsc', 'wsf', 'wsh', 'xll',
];
/**
 * I modelli della chat che ognuno può scegliere nel pannello (30/10/2026; Lorenzo: «un bottone per
 * cambiare modello? Da haiku a sonnet?»). Si parte da AI_MODEL. Sonnet costa venti volte Haiku
 * (prezzi.ts dell'API): il tetto del giorno finisce prima.
 */
export const MODELLI_CHAT = [
    { id: 'claude-haiku-5-5', nome: 'Haiku' },
    { id: 'claude-sonnet-5-5', nome: 'Sonnet' },
];
/** PUT /ai/modello. */
export const sceltaModelloInput = z.object({ modello: z.enum(['claude-haiku-5-5', 'claude-sonnet-5-5']) });
/** I codici delle rotte /ai/posta: il web sceglie da qui la frase. */
export const CODICI_POSTA = [
    'POSTA_NON_PRONTA', 'POSTA_NON_COLLEGATA', 'POSTA_DA_RICOLLEGARE', 'POSTA_OCCUPATA', 'POSTA_NON_RISPONDE',
    'POSTA_LETTURE_OGGI', 'POSTA_CONVERSAZIONE_SPARITA',
    'MAIL_DESTINATARIO', 'MAIL_TROPPI_DESTINATARI', 'MAIL_OGGI', 'MAIL_GIA_USATA', 'MAIL_SCADUTA',
    'MAIL_NON_PARTITA', 'MAIL_FORSE_PARTITA', 'MAIL_ALLEGATI', 'MAIL_ALLEGATO_TIPO',
];
/** Il ritorno da Google: `?posta=…` nell'indirizzo delle Impostazioni. */
export const ESITI_COLLEGAMENTO = ['collegata', 'negata', 'altra-casella', 'scaduto', 'permessi', 'errore'];
// ── La chat (Fase 2, os-planning/ai/fase-2-progetto.md, «Tipi condivisi») ──────
// Scritti prima degli agenti della 2B (29/10/2026), con quelli della 2D: fissano le forme.
/**
 * Il corpo di POST /ai/chat: resta un JSON piccolo (CORPO_AI, 64 KB), i byte dei file non ci
 * passano mai. Dalla 2F (29/10/2026) porta gli id degli allegati già caricati con POST
 * /ai/allegati, e il testo può mancare solo se c'è almeno un allegato.
 */
export const chatInput = z.object({
    /** null o assente: conversazione nuova. */
    conversazioneId: cuid().nullable().optional(),
    /** Vuoto solo con almeno un allegato (2F). */
    testo: z.string().trim().max(8000),
    /** location.pathname: la pagina aperta. */
    pagina: z.string().max(300).optional(),
    /** Gli id delle righe in attesa di AiAttachment (2F); un browser di prima non li manda. */
    allegati: z.array(z.string().regex(/^c[a-z0-9]{24}$/)).max(LIMITI_ALLEGATI.perDomanda).optional(),
})
    .refine((v) => v.testo.length > 0 || (v.allegati?.length ?? 0) > 0, { message: 'Scrivi una domanda o allega una foto o un PDF', path: ['testo'] })
    .refine((v) => !v.allegati || new Set(v.allegati).size === v.allegati.length, { message: 'Lo stesso allegato due volte', path: ['allegati'] });
//# sourceMappingURL=ai.js.map